import { defineStore } from 'pinia';
import { toRaw, markRaw } from 'vue';
import { type BlueprintFeatureSelection } from '~/models/blueprint';
import { type Feature, FeatureType, type FeatureOption, type FeatureOptions } from '~/models/feature';
import { useHomeWorldStore } from './homeworld';
import { usePropertyStore } from './property';
import { useCharacterStore } from './character';
import { property2FeatureOption, meetsRequires, type RequiresContext } from '~/models/property';

/**
 * Extract property ID from a memberRef.
 * MemberRefs stored in Firestore via JSON.stringify are plain objects with
 * the ID buried in _key.path.segments, not real DocumentReference instances.
 */
function getMemberRefId(ref: any): string | undefined {
	const raw = toRaw(ref);
	// Real DocumentReference
	if (raw?.id) return raw.id;
	// Serialized DocumentReference — ID is last segment
	const segments = raw?._key?.path?.segments;
	if (Array.isArray(segments) && segments.length > 0) {
		return segments[segments.length - 1];
	}
	return undefined;
}

export interface FeaturesState {
	selectedBlueprint: BlueprintFeatureSelection[] | null;
	featureTree: Feature[];
	selectedFeature: Feature | null;
	featureOptions: FeatureOptions;
	currentRank: number;
}

export const useFeaturesStore = defineStore('FeaturesStore', {
	state: (): FeaturesState => ({
		selectedBlueprint: null,
		featureTree: [],
		selectedFeature: null,
		featureOptions: [],
		currentRank: 0,
	}),

	getters: {
		/**
		 * Get features for the current rank
		 */
		currentRankFeatures: (state): Feature[] => {
			return state.featureTree;
		},

		/**
		 * Check if a feature is selected
		 */
		hasSelectedFeature: (state): boolean => {
			return state.selectedFeature !== null;
		},

		/**
		 * Get available feature options count
		 */
		optionsCount: (state): number => {
			return state.featureOptions.length;
		},
	},

	actions: {
		/**
		 * Set the active blueprint and build initial feature tree
		 */
		setBlueprint(blueprint: BlueprintFeatureSelection[]) {
			this.selectedBlueprint = blueprint;
			this.currentRank = 0;
			this.buildFeatureTree(0);
		},

		/**
		 * Set the current rank and rebuild feature tree
		 */
		setRank(rank: number) {
			this.currentRank = rank;
			this.buildFeatureTree(rank);
		},

		/**
		 * Build feature tree for a specific rank from the blueprint
		 */
		buildFeatureTree(rank: number) {
			if (!this.selectedBlueprint) {
				this.featureTree = [];
				return;
			}

			const rankFeatures = this.selectedBlueprint[rank];

			if (Array.isArray(rankFeatures)) {
				// TODO: Remove corp filter when Corp Blueprint feature is ready
				// Feature.requires mounts origin-specific features (e.g., Mutant Biology).
				const characterStore = useCharacterStore();
				const tags = characterStore.characterTags;
				this.featureTree = rankFeatures.filter((f: Feature) => {
					if (f.type === FeatureType.corp) return false;
					if (f.requires && f.requires.length > 0 && !meetsRequires(f.requires, tags)) return false;
					return true;
				});
				console.log(`Built feature tree for rank ${rank}:`, this.featureTree.length, 'features');
			} else {
				this.featureTree = [];
				console.warn(`No features found for rank ${rank}`);
			}
		},

		/**
		 * Select a feature and build its options
		 */
		async selectFeature(feature: Feature) {
			// Normalize to the raw object BEFORE storing/comparing. Pinia's setter
			// strips reactive proxies down to their raw target, so storing the
			// proxy and later comparing `this.selectedFeature !== feature` would
			// compare raw-vs-proxy and always look stale (empty option lists on
			// first click after a fresh page load).
			// markRaw prevents Vue/DevTools from deep-proxying the Feature object
			// (contains Firestore DocumentReferences with circular back-refs).
			const rawFeature = markRaw(toRaw(feature));
			this.selectedFeature = rawFeature;
			this.featureOptions = [];
			await this.buildFeatureOptions(rawFeature);
		},

		/**
		 * Clear feature selection
		 */
		clearFeatureSelection() {
			this.selectedFeature = null;
			this.featureOptions = [];
		},

		/**
		 * Build feature options based on the selected feature
		 * This is the core logic that replaces scattered component logic
		 */
		async buildFeatureOptions(feature: Feature): Promise<void> {
			console.log(`Building options for feature: ${feature.name} (${feature.type})`);

			// Skills: load all skill properties directly (no memberRefs needed)
			if (feature.type === FeatureType.skill) {
				const propertyStore = usePropertyStore();
				const skills = propertyStore.propertiesByType('skill');
				this.featureOptions = skills
					.sort((a, b) => a.name.localeCompare(b.name))
					.map(property2FeatureOption);
				console.log(`  → ${this.featureOptions.length} skill options available`);
				return;
			}

			// Skill Boons: filter by legacy requires (no memberRefs needed)
			if (feature.type === FeatureType.skillBoon) {
				const characterStore = useCharacterStore();
				this.featureOptions = characterStore.getSkillBoonFeatureOptions;
				console.log(`  → ${this.featureOptions.length} skill boon options available`);
				return;
			}

			// Stats: load +1 stat properties, strip level suffix for display
			if (feature.type === FeatureType.stats) {
				const propertyStore = usePropertyStore();
				const stats = propertyStore.propertiesByType('stats')
					.filter(s => s.id.endsWith('-1'));
				this.featureOptions = stats
					.sort((a, b) => a.name.localeCompare(b.name))
					.map(s => {
						const opt = property2FeatureOption(s);
						opt.name = s.name.replace(/\s+1$/, '');
						return opt;
					});
				console.log(`  → ${this.featureOptions.length} stat options available`);
				return;
			}

			// Check if feature has memberRefs or bundle memberRefIds (new system)
			if ((feature.memberRefs && feature.memberRefs.length > 0) || (feature._memberRefIds && feature._memberRefIds.length > 0)) {
				const options = await this.buildOptionsFromMemberRefs(feature);
				// Guard: abort if user selected a different feature while awaiting.
				// Compare raw objects — reactive proxies never equal their raw target.
				if (toRaw(this.selectedFeature) !== toRaw(feature)) return;
				this.featureOptions = options;
			} else {
				// Fallback to legacy system (no memberRefs)
				this.featureOptions = this.getLegacyOptions(feature);
			}

			console.log(`  → ${this.featureOptions.length} options available`);
		},

		/**
		 * Build options from memberRefs (new system with PropertyGroups data migrated to features)
		 */
		async buildOptionsFromMemberRefs(feature: Feature): Promise<FeatureOptions> {
			const options: FeatureOptions = [];
			const propertyStore = usePropertyStore();
			const { canViewProperty } = usePropertyVisibility();

			// Resolve member IDs from bundle _memberRefIds or Firestore memberRefs
			const memberIds: string[] = feature._memberRefIds?.length
				? feature._memberRefIds
				: (feature.memberRefs || []).map(ref => getMemberRefId(ref)).filter((id): id is string => !!id);

			// Types that don't need requires checking (no filtering beyond visibility)
			const noRequiresTypes = [
				FeatureType.core,
				FeatureType.origin,
				FeatureType.legacy,
				FeatureType.homeWorld,
				FeatureType.stats,
			];

			if (noRequiresTypes.includes(feature.type)) {
				// Origins, legacies and homeworlds are complete global lists: source
				// EVERY property of that type rather than the blueprint's curated
				// member list, so newly added content (e.g. a module's homeworlds or
				// legacies) shows up without having to edit the blueprint members.
				const globalListTypes: string[] = [
					FeatureType.origin, FeatureType.legacy, FeatureType.homeWorld,
				];
				if (globalListTypes.includes(feature.type)) {
					for (const prop of propertyStore.propertiesByType(feature.type as any)) {
						if (canViewProperty(prop)) {
							options.push(property2FeatureOption(prop));
						}
					}
					return options.sort((a, b) => a.name.localeCompare(b.name));
				}
				for (const id of memberIds) {
					const prop = propertyStore.propertyById(id);
					if (prop && canViewProperty(prop)) {
						options.push(property2FeatureOption(prop));
					}
				}
				return options.sort((a, b) => a.name.localeCompare(b.name));
			}

			// All other types: filter by requires tag cloud + threshold context
			const characterStore = useCharacterStore();
			const characterTags = characterStore.characterTags;
			const requiresContext: RequiresContext = characterStore.buildRequiresContext();

			for (const id of memberIds) {
				const prop = propertyStore.propertyById(id);
				if (prop && canViewProperty(prop) && meetsRequires(prop.requires, characterTags, requiresContext)) {
					options.push(property2FeatureOption(prop));
				}
			}
			return options.sort((a, b) => a.name.localeCompare(b.name));
		},

		/**
		 * Fallback for features without memberRefs (legacy system)
		 */
		getLegacyOptions(feature: Feature): FeatureOptions {
			console.log(`  Using legacy options for ${feature.type}`);
			const { filterPropertiesByVisibility } = usePropertyVisibility();

			switch (feature.type) {
				case FeatureType.origin: {
					// Origins are now stored as properties in the properties collection
					const propertyStore = usePropertyStore();
					const origins = propertyStore.propertiesByType('origin');
					return filterPropertiesByVisibility(origins).map(property2FeatureOption);
				}

				case FeatureType.legacy: {
					// Legacies are now stored as properties in the properties collection
					const propertyStore = usePropertyStore();
					const legacies = propertyStore.propertiesByType('legacy');
					return filterPropertiesByVisibility(legacies).map(property2FeatureOption);
				}

				case FeatureType.homeWorld: {
					const homeWorldStore = useHomeWorldStore();
					return homeWorldStore.getOptions;
				}

				case FeatureType.knack: {
					const characterStore = useCharacterStore();
					return characterStore.getKnackFeatureOptions;
				}

				case FeatureType.quirk: {
					const characterStore = useCharacterStore();
					return characterStore.getQuirkFeatureOptions;
				}

				case FeatureType.skillBoon: {
					const characterStore = useCharacterStore();
					return characterStore.getSkillBoonFeatureOptions;
				}

				case FeatureType.feat: {
					const characterStore = useCharacterStore();
					return characterStore.getFeatFeatureOptions || [];
				}

				case FeatureType.talent: {
					const characterStore = useCharacterStore();
					return characterStore.getTalentFeatureOptions || [];
				}

				case FeatureType.legacyItem: {
					const characterStore = useCharacterStore();
					return characterStore.getLegacyItemFeatureOptions;
				}

				default:
					console.warn(`Unhandled legacy feature type: ${feature.type}`);
					return [];
			}
		},

		/**
		 * Select a feature option and translate it to character property
		 * This bridges the gap between the feature selection UI and character state
		 */
		selectFeatureOption(option: FeatureOption) {
			if (!this.selectedFeature) {
				console.error('No feature selected');
				return;
			}

			console.log(`Selecting feature option: ${option.name} (${option.type})`);

			const characterStore = useCharacterStore();
			const rank = characterStore.rankPropertyRank;

			// Delegate to character store to handle the property selection
			// The character store knows how to translate feature options into character properties
			characterStore.selectPropertyOption(option, rank);

			console.log(`  → Property selected for character at rank ${rank}`);
		},

		/**
		 * Get feature by ID from current tree
		 */
		getFeatureById(featureId: string): Feature | undefined {
			return this.featureTree.find(f => f.id === featureId);
		},

		/**
		 * Check if feature has sub-features
		 */
		hasSubFeatures(feature: Feature): boolean {
			return feature.subFeatures && feature.subFeatures.length > 0;
		},

		/**
		 * Get sub-features for a feature
		 */
		getSubFeatures(feature: Feature): Feature[] {
			return feature.subFeatures || [];
		},

		/**
		 * Clear all state
		 */
		clear() {
			this.selectedBlueprint = null;
			this.featureTree = [];
			this.selectedFeature = null;
			this.featureOptions = [];
			this.currentRank = 0;
		},
	},
});
